// frontend/src/pages/admin/ManagePaymentsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
    FaSyncAlt, FaCheckCircle, FaHourglassHalf, FaPlus, 
    FaTrash, FaEdit, FaSave, FaTimes, FaSearch, FaFilter,
    FaCalendarAlt, FaListUl, FaExclamationTriangle, FaUserShield
} from 'react-icons/fa';

// MODAL COMPONENT
const DetailModal = ({ items, onClose }) => {
    if (!items) return null;
    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4 animate-fadeIn">
            <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md overflow-hidden shadow-2xl transform transition-all scale-100">
                <div className="bg-gray-800 p-4 flex justify-between items-center border-b border-gray-700">
                    <h3 className="text-white font-bold flex items-center gap-2"><FaListUl className="text-primary"/> Detaylar</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><FaTimes size={20} /></button>
                </div>
                <div className="p-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                    {items.length > 0 ? (
                        <ul className="space-y-2">
                            {items.map((item, idx) => (
                                <li key={idx} className="flex justify-between items-center bg-gray-800/50 p-3 rounded border border-gray-700/50">
                                    <span className="text-sm text-gray-300 w-2/3 truncate" title={item.name}>{item.name}</span>
                                    <span className="text-sm font-bold text-white font-mono">₺{item.price.toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                    ) : <p className="text-center text-gray-500 py-4">Detay yok.</p>}
                </div>
                <div className="bg-gray-800 p-4 border-t border-gray-700 text-right">
                    <div className="text-xs text-gray-400 mb-1">TOPLAM</div>
                    <div className="text-xl font-bold text-primary">₺{items.reduce((acc, curr) => acc + curr.price, 0).toFixed(2)}</div>
                </div>
            </div>
        </div>
    );
};

const ManagePaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [detailModalData, setDetailModalData] = useState(null);
  
  // Plan Ayarları
  const [isManualDuration, setIsManualDuration] = useState(false); 
  const [planSettings, setPlanSettings] = useState({ day: 15, duration: 12 });
  const [includeAdmins, setIncludeAdmins] = useState(false); 

  // Düzenleme
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [newPaymentMode, setNewPaymentMode] = useState(false);
  
  // YENİ: Manuel Ekleme Datası (Description kaldırıldı)
  const [newPaymentData, setNewPaymentData] = useState({ userId: '', amount: '', paymentDate: '' });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';
  const getCleanToken = () => localStorage.getItem('token')?.replace(/"/g, '');

  const fetchData = useCallback(async () => {
      try {
          const token = getCleanToken();
          const config = { headers: { Authorization: `Bearer ${token}` } };
          const [payRes, userRes] = await Promise.all([
              axios.get(`${API_URL}/payments`, config),
              axios.get(`${API_URL}/users`, config)
          ]);
          setPayments(payRes.data);
          setUsers(userRes.data);
          setLoading(false);
      } catch (error) { setLoading(false); }
  }, [API_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // HESAPLAMA GÜNCELLEMESİ (Muafiyet Filtresi)
  const activeUserCount = users.filter(u => {
      if (u.isActive === false) return false;
      if (u.isExempt === true) return false; // <-- MUAF OLANLARI SAYMA
      if (!includeAdmins && u.role === 'admin') return false; 
      return true;
  }).length;

  const handleGeneratePlan = async () => {
      const durationToSend = isManualDuration ? planSettings.duration : 0;
      const confirmMsg = `Plan oluşturulsun mu?\n\n- ${isManualDuration ? durationToSend + ' Ay Boyunca' : 'Kişi Sayısı Kadar Döngü'}\n- Adminler: ${includeAdmins ? 'DAHİL' : 'HARİÇ'}\n- Muaf Üyeler: HARİÇ`;
      
      if (!window.confirm(confirmMsg)) return;
      
      const token = getCleanToken();
      setLoading(true);
      try {
          const { data } = await axios.post(`${API_URL}/payments/generate`, 
            { 
                paymentDay: Number(planSettings.day), 
                planDuration: Number(durationToSend),
                includeAdmins: includeAdmins 
            }, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setMsg({ type: 'success', text: data.message });
          await fetchData();
      } catch (error) { 
          setMsg({ type: 'error', text: error.response?.data?.message || 'Hata.' });
      } finally { setLoading(false); }
  };

  const handleDeleteAll = async () => {
      if (!window.confirm("❗ TÜM KAYITLAR SİLİNECEK! Onaylıyor musunuz?")) return;
      const token = getCleanToken();
      setLoading(true);
      try {
          await axios.post(`${API_URL}/payments/delete-all?_method=DELETE`, {}, { headers: { Authorization: `Bearer ${token}` } });
          setMsg({ type: 'success', text: 'Tüm kayıtlar silindi.' });
          await fetchData();
      } catch (error) { setMsg({ type: 'error', text: 'Silinemedi.' }); } finally { setLoading(false); }
  };

  const toggleStatus = async (payment) => {
      try {
          const newStatus = payment.status === 'pending' ? 'completed' : 'pending';
          const token = getCleanToken();
          await axios.post(`${API_URL}/payments/${payment._id}/status?_method=PUT`, 
              { status: newStatus }, 
              { headers: { Authorization: `Bearer ${token}` } }
          );
          fetchData();
      } catch (error) { alert('Durum güncellenemedi.'); }
  };

  const startEdit = (payment) => {
      setEditingRow(payment._id);
      const dateStr = new Date(payment.paymentDate).toISOString().split('T')[0];
      setEditData({ amount: payment.amount, paymentDate: dateStr, status: payment.status });
  };

  const saveEdit = async (id) => {
      const token = getCleanToken();
      try { 
          await axios.post(`${API_URL}/payments/${id}?_method=PUT`, editData, { headers: { Authorization: `Bearer ${token}` } }); 
          setEditingRow(null); 
          fetchData(); 
      } catch (error) { alert('Hata'); }
  };

  const handleDelete = async (id) => {
      if(!window.confirm("Silinsin mi?")) return;
      const token = getCleanToken();
      try { await axios.post(`${API_URL}/payments/${id}?_method=DELETE`, {}, { headers: { Authorization: `Bearer ${token}` } }); fetchData(); } catch (error) { alert('Silinemedi'); }
  };
  
  // YENİ MANUEL EKLEME FONKSİYONU
  const handleCreateManual = async () => {
      if(!newPaymentData.userId || !newPaymentData.amount || !newPaymentData.paymentDate) return alert("Eksik bilgi: Üye, Tutar ve Tarih zorunludur.");
      
      const token = getCleanToken();
      try { 
          // Backend otomatik açıklama ekleyecek
          await axios.post(`${API_URL}/payments`, newPaymentData, { headers: { Authorization: `Bearer ${token}` } }); 
          setNewPaymentMode(false); 
          // Formu sıfırla
          setNewPaymentData({ userId: '', amount: '', paymentDate: '' });
          fetchData(); 
          setMsg({ type: 'success', text: 'Manuel ödeme eklendi, detaylar güncellendi.' });
      } catch (error) { 
          alert('Hata: ' + (error.response?.data?.message || 'Bilinmeyen hata')); 
      }
  };

  const filteredPayments = payments.filter(pay => {
      const searchLower = searchTerm.toLowerCase();
      const userName = pay.userId?.name?.toLowerCase() || '';
      return (userName.includes(searchLower) || pay.amount.toString().includes(searchLower)) && (filterStatus === 'all' || pay.status === filterStatus);
  });

  if (loading && payments.length === 0) return <div className="p-10 text-white animate-pulse">Yükleniyor...</div>;

  return (
    <div className="space-y-6 relative">
        {detailModalData && <DetailModal items={detailModalData} onClose={() => setDetailModalData(null)} />}

        {/* AYARLAR PANELI */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6 rounded-xl border border-border flex flex-col xl:flex-row gap-6 justify-between items-center shadow-lg">
            <div className="text-center xl:text-left">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2 justify-center xl:justify-start">
                   <FaCalendarAlt className="text-primary"/> Ödeme Yönetimi
                </h1>
                <p className="text-xs text-text-secondary mt-1">Nöbetleşe Adil Ödeme Sistemi</p>
            </div>
            
            <div className="flex flex-col lg:flex-row items-center gap-4 bg-gray-700/30 p-4 rounded-xl border border-gray-600/50">
                {/* 1. Gün */}
                <div className="flex flex-col items-center">
                    <label className="text-[10px] text-gray-400 font-bold mb-1">GÜN</label>
                    <div className="relative">
                        <input type="number" min="1" max="28" value={planSettings.day} onChange={e => setPlanSettings({...planSettings, day: e.target.value})} className="bg-gray-800 text-white border border-gray-600 rounded-lg px-2 py-2 w-16 text-center font-bold outline-none" />
                        <span className="absolute right-1 top-2 text-[10px] text-gray-500">.gün</span>
                    </div>
                </div>

                <div className="hidden lg:block w-px h-10 bg-gray-600"></div>

                {/* 2. Admin Dahil Checkbox */}
                <div className="flex flex-col items-center cursor-pointer" onClick={() => setIncludeAdmins(!includeAdmins)}>
                    <label className="text-[10px] text-gray-400 font-bold mb-1">ADMİN</label>
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${includeAdmins ? 'bg-primary/20 border-primary text-white' : 'bg-gray-800 border-gray-600 text-gray-500'}`}>
                        <FaUserShield size={14} />
                        <span className="text-xs font-bold">{includeAdmins ? 'DAHİL' : 'HARİÇ'}</span>
                    </div>
                </div>

                <div className="hidden lg:block w-px h-10 bg-gray-600"></div>

                {/* 3. Süre */}
                <div className="flex flex-col items-center">
                    <div className="flex items-center gap-2 mb-1">
                        <label className="text-[10px] text-gray-400 font-bold">SÜRE (AY)</label>
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => setIsManualDuration(!isManualDuration)}>
                            <div className={`w-3 h-3 rounded-sm border ${isManualDuration ? 'bg-primary border-primary' : 'border-gray-500'} flex items-center justify-center`}>
                                {isManualDuration && <div className="w-1.5 h-1.5 bg-white rounded-sm" />}
                            </div>
                            <span className="text-[9px] text-gray-400">Manuel</span>
                        </div>
                    </div>
                    <div className="relative group">
                        <input type="number" min="1" disabled={!isManualDuration} value={isManualDuration ? planSettings.duration : activeUserCount} onChange={e => setPlanSettings({...planSettings, duration: e.target.value})} className={`text-white border rounded-lg px-3 py-2 w-20 text-center font-bold outline-none transition-all ${isManualDuration ? 'bg-gray-800 border-primary' : 'bg-gray-700/50 border-gray-600 text-gray-400 cursor-not-allowed'}`} />
                        {!isManualDuration && <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">{activeUserCount} Kişi (Muaf Hariç)</div>}
                    </div>
                </div>

                <button onClick={handleGeneratePlan} className="ml-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white px-4 py-3 rounded-lg font-bold shadow-lg flex items-center gap-2 active:scale-95">
                    <FaSyncAlt className={loading ? "animate-spin" : ""} /> <span>OLUŞTUR</span>
                </button>
            </div>
        </div>

        {/* MESAJ */}
        {msg && <div className={`p-4 rounded-lg border flex justify-between items-center ${msg.type==='success'?'bg-green-500/10 border-green-500/30 text-green-400':'bg-red-500/10 border-red-500/30 text-red-400'}`}><span>{msg.text}</span><button onClick={()=>setMsg(null)}><FaTimes/></button></div>}

        {/* FİLTRE & ARAÇLAR */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-800/50 p-4 rounded-xl border border-gray-700">
            <div className="relative w-full md:w-1/3"><FaSearch className="absolute left-3 top-3 text-gray-500" /><input type="text" placeholder="Ara..." className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white outline-none" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
            <div className="flex gap-2">
                <div className="flex items-center bg-gray-900 p-1 rounded-lg border border-gray-700">{['all','pending','completed'].map(s=><button key={s} onClick={()=>setFilterStatus(s)} className={`px-3 py-1.5 rounded-md text-xs font-bold ${filterStatus===s?'bg-gray-700 text-white':'text-gray-500'}`}>{s==='all'?'Tümü':s==='pending'?'Bekleyen':'Ödenen'}</button>)}</div>
                <button onClick={handleDeleteAll} className="bg-red-600/20 hover:bg-red-600 text-red-500 hover:text-white border border-red-600/50 px-3 py-2 rounded-lg font-bold flex items-center gap-1 text-xs"><FaTrash /> Tümünü Sil</button>
                <button onClick={()=>setNewPaymentMode(!newPaymentMode)} className="bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-lg font-bold flex items-center gap-1 text-xs"><FaPlus /> Manuel</button>
            </div>
        </div>

        {/* MANUEL EKLEME FORM (YENİLENMİŞ) */}
        {newPaymentMode && (
            <div className="bg-gray-800 p-4 rounded-xl border border-green-500/30 grid grid-cols-1 md:grid-cols-4 gap-4 items-end animate-fadeIn">
                
                {/* 1. ÜYE SEÇİMİ */}
                <div>
                    <label className="text-xs text-gray-400 font-bold mb-1 block">Kullanıcı</label>
                    <select className="w-full bg-black/30 border border-gray-600 rounded p-2 text-white outline-none focus:border-green-500" 
                        value={newPaymentData.userId}
                        onChange={e=>setNewPaymentData({...newPaymentData, userId:e.target.value})}
                    >
                        <option value="">Seçiniz...</option>
                        {users.map(u => (
                            <option key={u._id} value={u._id}>
                                {u.name} {u.isExempt ? '(Muaf)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
                
                {/* 2. TARİH SEÇİMİ */}
                <div>
                    <label className="text-xs text-gray-400 font-bold mb-1 block">Ödeme Tarihi</label>
                    <input 
                        type="date" 
                        className="w-full bg-black/30 border border-gray-600 rounded p-2 text-white outline-none focus:border-green-500" 
                        value={newPaymentData.paymentDate}
                        onChange={e=>setNewPaymentData({...newPaymentData, paymentDate:e.target.value})} 
                    />
                </div>

                {/* 3. TUTAR GİRİŞİ */}
                <div>
                    <label className="text-xs text-gray-400 font-bold mb-1 block">Tutar (TL)</label>
                    <input 
                        type="number" 
                        placeholder="0.00"
                        className="w-full bg-black/30 border border-gray-600 rounded p-2 text-white outline-none focus:border-green-500" 
                        value={newPaymentData.amount}
                        onChange={e=>setNewPaymentData({...newPaymentData, amount:e.target.value})} 
                    />
                </div>

                {/* 4. KAYDET BUTONU */}
                <button onClick={handleCreateManual} className="bg-green-600 hover:bg-green-500 text-white p-2 rounded font-bold transition-colors h-[42px]">
                    KAYDET
                </button>
            </div>
        )}

        {/* TABLO */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden shadow-xl mt-4">
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-400">
                    <thead className="bg-gray-800 text-gray-300 uppercase font-bold text-xs">
                        <tr><th className="p-4">Tarih</th><th className="p-4">Üye</th><th className="p-4">Tutar</th><th className="p-4">İçerik</th><th className="p-4 text-center">Durum</th><th className="p-4 text-right">İşlemler</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {filteredPayments.map(pay => (
                            <tr key={pay._id} className="hover:bg-gray-800/50 transition-colors">
                                {/* TARİH */}
                                <td className="p-4 font-mono text-gray-300">
                                    {editingRow === pay._id ? 
                                    <input type="date" className="bg-gray-700 text-white rounded p-1" value={editData.paymentDate} onChange={e => setEditData({...editData, paymentDate: e.target.value})} /> 
                                    : new Date(pay.paymentDate).toLocaleDateString('tr-TR')}
                                </td>
                                <td className="p-4 font-bold text-white">{pay.userId?.name || 'Silinmiş'}</td>
                                {/* TUTAR */}
                                <td className="p-4 text-white font-bold">
                                    {editingRow === pay._id ? 
                                    <input type="number" className="bg-gray-700 text-white rounded p-1 w-20" value={editData.amount} onChange={e => setEditData({...editData, amount: e.target.value})} /> 
                                    : `₺${pay.amount.toFixed(2)}`}
                                </td>
                                <td className="p-4"><button onClick={() => setDetailModalData(pay.includedSubscriptions)} className="text-xs text-blue-400 hover:text-blue-300 border-b border-dotted border-blue-500/50"><FaListUl className="inline mr-1" />{pay.includedSubscriptions?.length} Kalem</button></td>
                                
                                {/* DURUM */}
                                <td className="p-4 text-center">
                                    {editingRow === pay._id ? (
                                        <select className="bg-gray-700 text-white text-xs rounded p-1 border border-gray-500 outline-none" value={editData.status} onChange={e => setEditData({...editData, status: e.target.value})}>
                                            <option value="pending">Bekliyor</option>
                                            <option value="completed">Ödendi</option>
                                        </select>
                                    ) : (
                                        <button onClick={() => toggleStatus(pay)} className={`px-3 py-1 rounded-full text-[10px] uppercase font-bold flex items-center gap-1.5 mx-auto border ${pay.status === 'completed' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}>
                                            {pay.status === 'completed' ? <><FaCheckCircle /> Ödendi</> : <><FaHourglassHalf /> Bekliyor</>}
                                        </button>
                                    )}
                                </td>
                                
                                <td className="p-4 text-right">
                                    {editingRow === pay._id ? 
                                    <><button onClick={() => saveEdit(pay._id)} className="text-green-400 mr-2"><FaSave size={18}/></button><button onClick={() => setEditingRow(null)} className="text-gray-500"><FaTimes size={18}/></button></> 
                                    : <><button onClick={() => startEdit(pay)} className="text-blue-400 mr-2"><FaEdit size={18}/></button><button onClick={() => handleDelete(pay._id)} className="text-red-400"><FaTrash size={18}/></button></>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredPayments.length === 0 && <div className="p-8 text-center text-gray-500">Kayıt yok.</div>}
            </div>
        </div>
    </div>
  );
};

export default ManagePaymentsPage;